"use client"

import { useState, useEffect, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { CircleNotch as Loader2, Plus, ArrowsClockwise as RefreshCw, Copy, Scroll as ScrollText } from "@phosphor-icons/react"
import { apiRequestWithAuth, getStoredToken } from "@/lib/api-config"

const TASK_TYPES = [
  { value: "rogue", label: "肉鸽·傀影" },
  { value: "rogue2", label: "肉鸽·水月" },
  { value: "rogue3", label: "肉鸽·萨米" },
  { value: "rogue4", label: "肉鸽·萨卡兹" },
  { value: "rogue5", label: "肉鸽·界园" },
  { value: "rogue6", label: "肉鸽·黑流树海" },
  { value: "sand_fire", label: "生息·沙洲" },
  { value: "sand_fire2", label: "生息·锚点" },
  { value: "daily", label: "日常" },
]

const ROGUE_PARAMS = [
  { label: "满级且满源石锭 (max|999)", value: "max|999" },
  { label: "仅满级 (max|0)", value: "max|0" },
  { label: "仅源石锭 (0|999)", value: "0|999" },
  { label: "自定义", value: "custom" },
]

function labelOfTaskType(v: string) {
  return TASK_TYPES.find((t) => t.value === v)?.label || v
}

// 子用户状态判定（严格按 ark 网站规则）：只有用户主动点了删除按钮后 account 字段变成 "delete" 才是「删除」；其余过期子用户都是「过期」
function subUserStatus(u: any): { label: string; cls: string } {
  if (u?.account === "delete") {
    return { label: "删除", cls: "bg-red-100 text-red-700 border border-red-200" }
  }
  if (u?.expireTime) {
    const t = new Date(u.expireTime).getTime()
    if (!isNaN(t) && t < Date.now()) {
      return { label: "过期", cls: "bg-yellow-100 text-yellow-800 border border-yellow-200" }
    }
  }
  return { label: "正常", cls: "bg-blue-100 text-blue-700 border border-blue-200" }
}

function isDaily(v: string) {
  return v === "daily"
}
function isRogueLike(v: string) {
  return v.startsWith("rogue") || v.startsWith("sand_fire")
}

// 动森会主题卡片（与 admin/dashboard 同步的圆点纹理 + 边框配色）
const patterns = {
  "app-teal":   { border: "var(--leaf-mint)", bg: "var(--leaf-50)", color: "var(--leaf-800)", dot1: "rgba(130,213,187,0.18)", dot2: "rgba(170,235,210,0.12)" },
  "app-yellow":  { border: "var(--gold-400)", bg: "var(--gold-100)", color: "var(--gold-700)", dot1: "rgba(247,205,103,0.18)", dot2: "rgba(255,230,160,0.12)" },
  "warm-peach-pink": { border: "var(--ember-300)", bg: "var(--ember-50)", color: "var(--ember-600)", dot1: "rgba(225,140,111,0.18)", dot2: "rgba(255,185,160,0.12)" },
  "lime-green": { border: "var(--lime-500)", bg: "var(--gold-50)", color: "var(--gold-600)", dot1: "rgba(209,218,73,0.18)", dot2: "rgba(230,240,130,0.12)" },
  "app-blue":   { border: "var(--sky-400)", bg: "var(--sky-50)", color: "var(--sky-700)", dot1: "rgba(136,157,240,0.18)", dot2: "rgba(180,195,255,0.12)" },
}
function patStyles(key: keyof typeof patterns) {
  const p = patterns[key];
  return {
    border: "1.5px solid " + p.border,
    borderRadius: 16,
    background:
      "radial-gradient(circle, " + p.dot1 + " 1.5px, transparent 1.5px), radial-gradient(circle, " + p.dot2 + " 1px, transparent 1px), " + p.bg,
    backgroundSize: "28px 28px, 14px 14px",
    backgroundPosition: "0 0, 7px 7px",
  };
}

export default function UpstreamArkPage() {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)

  // 看板
  const [dash, setDash] = useState<any>(null)

  // 生成 CDK
  const [cdkType, setCdkType] = useState("rogue6")
  const [cdkParamPreset, setCdkParamPreset] = useState("max|999")
  const [cdkLevel, setCdkLevel] = useState("max")
  const [cdkCoin, setCdkCoin] = useState(999)
  const [cdkCount, setCdkCount] = useState(1)
  const [cdkResult, setCdkResult] = useState<string[]>([])
  const [cdkBalance, setCdkBalance] = useState<string>("")
  const [creatingCdk, setCreatingCdk] = useState(false)

  // 创建代打用户
  const [suAccount, setSuAccount] = useState("")
  const [suPassword, setSuPassword] = useState("")
  const [suServer, setSuServer] = useState("0")
  const [suType, setSuType] = useState("rogue6")
  const [suParamPreset, setSuParamPreset] = useState("max|999")
  const [suLevel, setSuLevel] = useState("max")
  const [suCoin, setSuCoin] = useState(999)
  const [suName, setSuName] = useState("")
  const [creatingSu, setCreatingSu] = useState(false)

  // 续费 / 换任务
  const [rnAccount, setRnAccount] = useState("")
  const [rnSubId, setRnSubId] = useState("")
  const [rnCdk, setRnCdk] = useState("")
  const [renewing, setRenewing] = useState(false)
  // 列表行"续费"按钮触发的弹窗模式
  const [renewDialogOpen, setRenewDialogOpen] = useState(false)
  const [renewTarget, setRenewTarget] = useState<any>(null)

  // 用户列表
  const [kw, setKw] = useState("")
  const [subList, setSubList] = useState<any[]>([])
  const [loadingList, setLoadingList] = useState(false)

  // 日志
  const [logModalOpen, setLogModalOpen] = useState(false)
  const [logUserId, setLogUserId] = useState<number | null>(null)
  const [logRows, setLogRows] = useState<any[]>([])
  const [loadingLog, setLoadingLog] = useState(false)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const toggleExp = (i: number) =>
    setExpanded(prev => {
      const s = new Set(prev)
      if (s.has(i)) s.delete(i); else s.add(i)
      return s
    })

  const token = getStoredToken()

  const loadDashboard = async () => {
    if (!token) return
    try {
      const r: any = await apiRequestWithAuth("/ark/dashboard", token)
      if (r.code === 200) setDash(r.data)
      else toast({ variant: "destructive", title: "看板加载失败", description: r.msg })
    } catch (e: any) {
      toast({ variant: "destructive", title: "看板加载失败", description: e.message })
    }
  }

  useEffect(() => {
    loadDashboard()
  }, [])

  // ---- 计算 param ----
  function computeParam(type: string, preset: string, level: string, coin: number) {
    if (isDaily(type)) return String(level)
    if (preset === "custom") return `${level}|${coin}`
    return preset
  }

  // ---- 生成 CDK ----
  const handleCreateCdk = async () => {
    if (!token) return
    setCreatingCdk(true)
    try {
      const param = computeParam(cdkType, cdkParamPreset, cdkLevel, cdkCoin)
      const r: any = await apiRequestWithAuth("/ark/cdk", token, {
        method: "POST",
        body: JSON.stringify({ type: cdkType, param, count: cdkCount }),
      })
      if (r.code === 200) {
        setCdkResult(r.data.newCdks || [])
        setCdkBalance(r.data.balance ?? "")
        toast({ title: "CDK 创建成功", description: `已生成 ${r.data.count} 张，请复制。已真实扣费。` })
        loadDashboard()
      } else {
        toast({ variant: "destructive", title: "创建失败", description: r.msg })
      }
    } catch (e: any) {
      toast({ variant: "destructive", title: "创建失败", description: e.message })
    } finally {
      setCreatingCdk(false)
    }
  }

  const copyCdk = (v: string) => {
    navigator.clipboard?.writeText(v)
    toast({ title: "已复制", description: v.slice(0, 12) + "..." })
  }

  // ---- 创建代打用户 ----
  const handleCreateSubUser = async () => {
    if (!token) return
    if (!suAccount || !suPassword) {
      toast({ variant: "destructive", title: "账号和密码必填" })
      return
    }
    setCreatingSu(true)
    try {
      const param = computeParam(suType, suParamPreset, suLevel, suCoin)
      const r: any = await apiRequestWithAuth("/ark/subuser", token, {
        method: "POST",
        body: JSON.stringify({
          account: suAccount,
          password: suPassword,
          server: Number(suServer),
          taskType: suType,
          level: suLevel,
          coin: suCoin,
          param,
          name: suName || undefined,
        }),
      })
      if (r.code === 200) {
        toast({ title: "创建成功", description: `子用户 id=${r.data.subUserId}，已消耗 1 张 ${suType} CDK` })
        setSuAccount(""); setSuPassword(""); setSuName("")
        loadDashboard()
        fetchList()
      } else {
        toast({ variant: "destructive", title: "创建失败", description: r.msg })
      }
    } catch (e: any) {
      toast({ variant: "destructive", title: "创建失败", description: e.message })
    } finally {
      setCreatingSu(false)
    }
  }

  // ---- 续费 / 换任务 ----
  const handleRenew = async (override?: { subUserId?: number; account?: string }) => {
    if (!token) return
    const cdk = rnCdk.trim()
    if (!cdk) {
      toast({ variant: "destructive", title: "请填写激活码 / CDK" })
      return
    }
    const payload: any = { cdk }
    if (override?.subUserId) payload.subUserId = override.subUserId
    else if (override?.account) payload.account = override.account
    else if (rnSubId) payload.subUserId = Number(rnSubId)
    else if (rnAccount) payload.account = rnAccount
    if (!payload.subUserId && !payload.account) {
      toast({ variant: "destructive", title: "请填写子用户 ID 或游戏账号" })
      return
    }
    setRenewing(true)
    try {
      const r: any = await apiRequestWithAuth("/ark/subuser/renew", token, {
        method: "POST",
        body: JSON.stringify(payload),
      })
      if (r.code === 200) {
        toast({ title: "续费/激活成功", description: r.msg })
        setRnCdk("")
        if (override) {
          setRenewDialogOpen(false)
          fetchList()
        } else {
          setRnSubId(""); setRnAccount("")
        }
        loadDashboard()
      } else {
        toast({ variant: "destructive", title: "激活失败", description: r.msg })
      }
    } catch (e: any) {
      toast({ variant: "destructive", title: "失败", description: e.message })
    } finally {
      setRenewing(false)
    }
  }

  // ---- 用户列表 ----
  const fetchList = async () => {
    if (!token) return
    setLoadingList(true)
    try {
      const q = kw ? `?keyword=${encodeURIComponent(kw)}&size=50` : "?size=50"
      const r: any = await apiRequestWithAuth("/ark/subusers" + q, token)
      if (r.code === 200) {
        setSubList(r.data.records || [])
      } else {
        toast({ variant: "destructive", title: "列表加载失败", description: r.msg })
      }
    } catch (e: any) {
      toast({ variant: "destructive", title: "列表加载失败", description: e.message })
    } finally {
      setLoadingList(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- 日志 ----
  const openLog = async (userId: number) => {
    if (!token) return
    setLogUserId(userId)
    setLogModalOpen(true)
    setLoadingLog(true)
    try {
      const r: any = await apiRequestWithAuth(`/ark/subuser/log?userId=${userId}&page=1&size=30`, token)
      if (r.code === 200) {
        setLogRows(r.data.records || [])
        setExpanded(new Set())
      } else {
        toast({ variant: "destructive", title: "日志加载失败", description: r.msg })
      }
    } catch (e: any) {
      toast({ variant: "destructive", title: "日志加载失败", description: e.message })
    } finally {
      setLoadingLog(false)
    }
  }

  function logText(r: any) {
    return r?.content || r?.title || r?.detail || r?.msg || JSON.stringify(r)
  }

  const invList = useMemo(() => (dash?.inventory?.cdkList as any[]) || [], [dash])
  // 未使用的 CDK 列表，供续费处下拉选
  const unusedCdkOptions = useMemo(
    () => (dash?.inventory?.cdkList as any[] || [])
      .filter((c: any) => !c.used)
      .map((c: any) => ({
        value: c.cdk as string,
        label: `${c.type || ''} · ${c.param || '-'} · ${(c.cdk || '').slice(0, 16)}…`,
      })),
    [dash]
  )

  return (
    <>
      <div className="mb-6">
        <div className="page-title-box">
          <div className="page-title">上游代打（ark）</div>
          <div className="page-subtitle">生成 CDK · 创建代打用户 · 续费换任务 · 日志监控</div>
        </div>
      </div>

      {/* 看板 */}
      <Card className="mb-6" style={patStyles("app-teal")}>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>ark 看板</CardTitle>
            <CardDescription>余额 / 折扣 / 过期 / CDK 库存</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadDashboard(); fetchList() }}>
            <RefreshCw className="mr-2 h-4 w-4" /> 刷新
          </Button>
        </CardHeader>
        <CardContent>
          {dash ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {/* 四张指标卡用 patStyles 同款色系，避开外层 app-teal，每张加左侧色带强调 */}
              <div className="rounded-lg bg-sky2-50 border-l-4 border-l-[var(--sky-400)] p-3">
                <div className="text-xs text-muted-foreground">余额</div>
                <div className="text-xl font-bold">{String(dash.proUser?.balance ?? "-")}</div>
              </div>
              <div className="rounded-lg bg-gold-50 border-l-4 border-l-[var(--lime-500)] p-3">
                <div className="text-xs text-muted-foreground">折扣(discount)</div>
                <div className="text-xl font-bold">{String(dash.proUser?.discount ?? "-")}</div>
              </div>
              <div className="rounded-lg bg-gold-100 border-l-4 border-l-[var(--gold-400)] p-3">
                <div className="text-xs text-muted-foreground">过期时间</div>
                <div className="text-xl font-bold">{String(dash.proUser?.expireTime ?? "-")}</div>
              </div>
              <div className="rounded-lg bg-ember-50 border-l-4 border-l-[var(--ember-300)] p-3">
                <div className="text-xs text-muted-foreground">CDK 库存</div>
                <div className="text-xl font-bold">{invList.length} 张</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">加载中…</div>
          )}
          {invList.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>CDK</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>参数</TableHead>
                  <TableHead>是否使用</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invList.map((c: any) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.id}</TableCell>
                    <TableCell className="font-mono text-xs">{c.cdk}</TableCell>
                    <TableCell>{labelOfTaskType(c.type)}</TableCell>
                    <TableCell>{c.param}</TableCell>
                    <TableCell>{c.used === 1 ? "已用" : "未用"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 生成 CDK */}
      <Card className="mb-6" style={patStyles("app-yellow")}>
        <CardHeader>
          <CardTitle>生成 CDK</CardTitle>
          <CardDescription>⚠️ 会真实扣费（余额以 ark 返回为准）</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>任务类型</Label>
              <Select value={cdkType} onValueChange={setCdkType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isDaily(cdkType) ? (
              <div className="space-y-2">
                <Label>天数</Label>
                <Input type="number" value={cdkLevel} onChange={(e) => setCdkLevel(e.target.value)} />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>参数预设</Label>
                <Select value={cdkParamPreset} onValueChange={setCdkParamPreset}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROGUE_PARAMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {cdkParamPreset === "custom" && (
                  <div className="flex gap-2">
                    <Input placeholder="等级" value={cdkLevel} onChange={(e) => setCdkLevel(e.target.value)} />
                    <Input type="number" placeholder="源石锭" value={cdkCoin} onChange={(e) => setCdkCoin(Number(e.target.value) || 0)} />
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>生成数量</Label>
              <Input type="number" min={1} value={cdkCount} onChange={(e) => setCdkCount(Number(e.target.value) || 1)} />
            </div>
          </div>
          <Button onClick={handleCreateCdk} disabled={creatingCdk}>
            {creatingCdk && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Plus className="mr-2 h-4 w-4" /> 生成 CDK
          </Button>
          {cdkResult.length > 0 && (
            <div className="rounded-lg border p-3 space-y-2">
              <div className="text-sm font-semibold">新生成的 CDK（点击复制）：</div>
              {cdkResult.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input readOnly value={v} className="font-mono" />
                  <Button size="sm" variant="outline" onClick={() => copyCdk(v)}><Copy className="h-4 w-4" /></Button>
                </div>
              ))}
              <div className="text-xs text-muted-foreground">当前余额：{cdkBalance}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 创建代打用户 */}
      <Card className="mb-6" style={patStyles("warm-peach-pink")}>
        <CardHeader>
          <CardTitle>创建代打用户（首次开单）</CardTitle>
          <CardDescription>先建 1 张 CDK 再建号，会真实扣费；同一游戏账号只能有一个子用户槽位</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>游戏账号</Label>
              <Input value={suAccount} onChange={(e) => setSuAccount(e.target.value)} placeholder="如 13411964836" />
            </div>
            <div className="space-y-2">
              <Label>游戏密码</Label>
              <Input value={suPassword} onChange={(e) => setSuPassword(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>区服</Label>
              <Select value={suServer} onValueChange={setSuServer}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">官服</SelectItem>
                  <SelectItem value="1">B服</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>任务类型</Label>
              <Select value={suType} onValueChange={setSuType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isDaily(suType) ? (
              <div className="space-y-2">
                <Label>天数</Label>
                <Input value={suLevel} onChange={(e) => setSuLevel(e.target.value)} />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>参数预设</Label>
                <Select value={suParamPreset} onValueChange={setSuParamPreset}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROGUE_PARAMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {suParamPreset === "custom" && (
                  <div className="flex gap-2">
                    <Input placeholder="等级" value={suLevel} onChange={(e) => setSuLevel(e.target.value)} />
                    <Input type="number" placeholder="源石锭" value={suCoin} onChange={(e) => setSuCoin(Number(e.target.value) || 0)} />
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>名称（可选）</Label>
              <Input value={suName} onChange={(e) => setSuName(e.target.value)} placeholder="默认 ark_{账号}" />
            </div>
          </div>
          <Button onClick={handleCreateSubUser} disabled={creatingSu}>
            {creatingSu && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            创建代打用户
          </Button>
        </CardContent>
      </Card>

      {/* 续费 / 换任务（ark 风格：传 CDK 激活到已有子用户，不扣费） */}
      <Card className="mb-6" style={patStyles("lime-green")}>
        <CardHeader>
          <CardTitle>续费 / 换任务</CardTitle>
          <CardDescription>用一张已有 CDK 激活到子用户槽位（任务类型由 CDK 自动切换，不扣费）</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>子用户 ID（可选，优先）</Label>
              <Input value={rnSubId} onChange={(e) => setRnSubId(e.target.value)} placeholder="留空则按账号查" />
            </div>
            <div className="space-y-2">
              <Label>游戏账号（可选）</Label>
              <Input value={rnAccount} onChange={(e) => setRnAccount(e.target.value)} placeholder="子用户 ID 留空时使用" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>激活码 / CDK</Label>
              <Select value={rnCdk} onValueChange={setRnCdk}>
                <SelectTrigger><SelectValue placeholder="从库存未用 CDK 中选择" /></SelectTrigger>
                <SelectContent>
                  {unusedCdkOptions.length > 0 ? unusedCdkOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  )) : (
                    <SelectItem value="__empty__" disabled>库存暂无未用 CDK，请先到「生成 CDK」建卡</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={() => handleRenew()} disabled={renewing}>
            {renewing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            续费 / 激活
          </Button>
        </CardContent>
      </Card>

      {/* 用户列表 + 日志 */}
      <Card className="mb-6" style={patStyles("app-blue")}>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>代打用户列表</CardTitle>
            <CardDescription>子用户即游戏账号的持久槽位</CardDescription>
          </div>
          <div className="flex gap-2">
            <Input placeholder="按账号搜索" value={kw} onChange={(e) => setKw(e.target.value)} className="w-40" />
            <Button variant="outline" size="sm" onClick={fetchList}><RefreshCw className="mr-2 h-4 w-4" /> 查询</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingList ? (
            <div className="flex items-center h-24"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>名称</TableHead>
                  <TableHead>账号</TableHead>
                  <TableHead>任务类型</TableHead>
                  <TableHead>区服</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>到期</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subList.length > 0 ? subList.map((u: any) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.id}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.account}</TableCell>
                    <TableCell>{labelOfTaskType(u.taskType)}</TableCell>
                    <TableCell>{u.server === 1 ? "B服" : "官服"}</TableCell>
                    <TableCell>{(() => { const s = subUserStatus(u); return <span className={"px-2 py-0.5 rounded text-xs " + s.cls}>{s.label}</span> })()}</TableCell>
                    <TableCell>{u.expireTime}</TableCell>
<TableCell>
                  <Button size="sm" variant="outline" className="mr-1" onClick={() => openLog(u.id)}><ScrollText className="mr-2 h-4 w-4" /> 日志</Button>
                  <Button size="sm" variant="outline" className="text-orange-700 border-orange-300 hover:bg-orange-50" onClick={() => { setRenewTarget(u); setRnCdk(""); setRenewDialogOpen(true); }}>续费</Button>
                </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">无数据</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={logModalOpen} onOpenChange={setLogModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>作战日志（用户 {logUserId}）</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto space-y-2">
            {loadingLog ? (
              <div className="flex items-center"><Loader2 className="h-5 w-5 animate-spin" /> 加载中…</div>
            ) : logRows.length > 0 ? logRows.map((r: any, i: number) => {
              const open = expanded.has(i)
              const lvl = (r.level || "").toUpperCase()
              return (
                <div key={i} className="rounded border bg-white/60">
                  <button
                    onClick={() => toggleExp(i)}
                    className="w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-white/80"
                  >
                    <span className="w-3 inline-block text-base font-bold text-slate-500 text-center">{open ? "−" : "+"}</span>
                    <span className={
                      "text-[10px] px-1.5 py-0.5 rounded font-bold " +
                      (lvl === "WARN" ? "bg-orange-200 text-orange-800" :
                       lvl === "ERROR" ? "bg-red-200 text-red-800" :
                       lvl === "INFO" ? "bg-blue-200 text-blue-800" :
                       "bg-slate-200 text-slate-700")
                    }>{lvl || "INFO"}</span>
                    <span className="flex-1 truncate">{logText(r)}</span>
                    <span className="text-muted-foreground text-xs whitespace-nowrap">{r.createTime}</span>
                  </button>
                  {open && (
                    <div className="px-7 pb-3 pt-2 text-sm space-y-2 border-t border-white/60">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <div>账号：{r.account || "—"}</div>
                        <div>服务器：{r.server === 1 ? "B服" : r.server === 0 ? "官服" : "—"}</div>
                        <div>用户名：{r.name || "—"}</div>
                        <div>任务类型：{r.taskType || "—"}</div>
                      </div>
                      {r.detail && (
                        <div className="whitespace-pre-wrap text-slate-700 text-xs leading-relaxed">{r.detail}</div>
                      )}
                      {r.imageUrl && (
                        <a href={r.imageUrl} target="_blank" rel="noreferrer">
                          <img src={r.imageUrl} alt="screenshot" className="rounded border max-w-full max-h-72 object-contain bg-slate-50" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )
            }) : (
              <div className="text-center text-muted-foreground">暂无日志</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 续费弹窗（ark 风格：列表行点续费 → 只填 CDK） */}
      <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>续费用户</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {renewTarget && (
              <div className="text-sm text-muted-foreground bg-slate-50 rounded p-2">
                <div>账号：<span className="font-medium text-slate-700">{renewTarget.account}</span></div>
                <div>当前任务：{labelOfTaskType(renewTarget.taskType)}</div>
                <div>ID：{renewTarget.id}</div>
              </div>
            )}
            <div className="space-y-2">
              <Label>使用激活码</Label>
              <Select value={rnCdk} onValueChange={setRnCdk}>
              <SelectTrigger><SelectValue placeholder="从库存未用 CDK 中选择" /></SelectTrigger>
              <SelectContent>
                {unusedCdkOptions.length > 0 ? unusedCdkOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                )) : (
                  <SelectItem value="__empty__" disabled>库存暂无未用 CDK，请先到「生成 CDK」建卡</SelectItem>
                )}
              </SelectContent>
            </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setRenewDialogOpen(false)}>取 消</Button>
            <Button onClick={() => renewTarget && handleRenew({ subUserId: renewTarget.id })} disabled={renewing}>
              {renewing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              确 定
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
